/**
 * STT adapter — the drop-in replacement for voice-talk.ts createVoiceTalk.
 *
 * bubble.tsx calls createSttVoiceTalk with the EXACT legacy options contract
 * ({onState,onFinal,onInterim,lang,silenceMs}) and gets the EXACT legacy
 * VoiceTalkHandle back. Engine selection happens here:
 *
 *   "whisper"  — whisper WASM engine (mic via acquireMic, model preloaded
 *                via warmStt); any failure this session downgrades to browser
 *   "browser"  — legacy SpeechRecognition (today's behavior, rollback lever)
 *   "auto"     — whisper when capable (wasm + mic + assets configured),
 *                else browser; UNKNOWN values behave like auto so old
 *                bundles never break when new engine names appear.
 *
 * The whisper session is created per-listen (matching createVoiceTalk's
 * lifecycle); the RUNTIME (worker + model) is a singleton warmed at AI Voice
 * session start (warmStt) so the first listen isn't cold.
 */
import { createVoiceTalk, isVoiceTalkSupported, setHighPrecisionStt, type VoiceTalkHandle, type VoiceTalkState } from "../voice-talk";
import type { SttSession, SttState } from "./engine";
import { acquireMic, releaseMic } from "./mic";
import {
  createWhisperEngine,
  createWhisperRuntime,
  type WhisperLoadProgress,
  type WhisperRuntime,
} from "./whisper";

export type SttEngineChoice = "auto" | "browser" | "whisper";

/** Pinned asset version — bump when tools/whisper-wasm cuts new artifacts. */
export const STT_ASSETS_VERSION = "v1.8.3-st1";

let engineChoice: string = "auto";
let assetOrigin: string | null = null;
let runtime: WhisperRuntime | null = null;
/** Sticky per-session downgrade after a whisper failure. */
let whisperBroken = false;
/** The in-flight (or settled) runtime+model load — see sttReady(). */
let warmPromise: Promise<void> | null = null;
/** True once the model can actually transcribe this session. */
let whisperLoaded = false;

/** Config chain entry (snippet ConfigMessage apply — mirrors setTtsVoice). */
export function setSttEngine(choice: string): void {
  engineChoice = choice || "auto";
}

/** The widget-api origin STT assets are served from (set with tts origin). */
export function setSttAssetOrigin(origin: string): void {
  const o = origin.replace(/\/$/, "").trim();
  assetOrigin = o || null;
}

function wasmSimdSupported(): boolean {
  try {
    // v128 probe — minimal SIMD module.
    return WebAssembly.validate(
      new Uint8Array([0, 97, 115, 109, 1, 0, 0, 0, 1, 5, 1, 96, 0, 1, 123, 3, 2, 1, 0, 10, 10, 1, 8, 0, 65, 0, 253, 15, 253, 98, 11]),
    );
  } catch {
    return false;
  }
}

function whisperCapable(): boolean {
  return (
    !whisperBroken &&
    assetOrigin !== null &&
    typeof Worker !== "undefined" &&
    typeof navigator !== "undefined" &&
    !!navigator.mediaDevices?.getUserMedia &&
    wasmSimdSupported()
  );
}

function wantWhisper(): boolean {
  if (engineChoice === "browser") return false;
  if (engineChoice === "whisper") return whisperCapable();
  return whisperCapable(); // "auto" + unknown values
}

function getRuntime(): WhisperRuntime {
  if (!runtime) {
    const base = `${assetOrigin}/api/widget/stt/${STT_ASSETS_VERSION}`;
    runtime = createWhisperRuntime({
      runtimeUrl: `${base}/libwhisper-st.js`,
      modelUrl: `${base}/ggml-tiny.en-q5_1.bin`,
    });
  }
  return runtime;
}

/**
 * Preload the whisper runtime+model (call on AI Voice session start so the
 * first listen isn't cold). Safe no-op when whisper won't be used. Failure
 * marks whisper broken for the session → auto paths fall back to browser SR.
 */
export function warmStt(onProgress?: (p: WhisperLoadProgress) => void): void {
  // New AI Voice session = clean slate: a transient failure must not brick
  // whisper until page reload (bench catch — on browsers without
  // SpeechRecognition, "fallback" was a dead mic forever).
  whisperBroken = false;
  if (!wantWhisper()) return;
  warmPromise = getRuntime()
    .load(onProgress)
    .then(() => {
      whisperLoaded = true;
    })
    .catch((err: unknown) => {
      try { console.warn("[BlueHat stt] runtime load failed:", String(err)); } catch { /* ignore */ }
      whisperBroken = true;
      warmPromise = null; // allow a retry on the next listen
    });
}

/**
 * True when a listen would start against an engine that CANNOT transcribe yet
 * — i.e. whisper is the engine and its 32MB model is still downloading.
 *
 * The live failure this exists for (2026-08-05, bluehatai.ai): capture began
 * and the 10s listen countdown armed while the model was still fetching. The
 * visitor spoke into a mic that could not hear, saw nothing (whisper emits no
 * textual interims), and the window expired into "Didn't catch that" — a dead
 * mic on every first use in a fresh browser profile.
 */
export function sttNeedsWarm(): boolean {
  return wantWhisper() && !whisperLoaded;
}

/**
 * Resolves when the engine can actually transcribe. Immediate when whisper
 * isn't the engine (browser SpeechRecognition needs no model) and on the
 * failure path (the adapter has already downgraded, so the caller should
 * proceed rather than hang).
 */
export function sttReady(): Promise<void> {
  if (!wantWhisper() || whisperLoaded) return Promise.resolve();
  if (!warmPromise) {
    warmPromise = getRuntime()
      .load()
      .then(() => {
        whisperLoaded = true;
      })
      .catch(() => {
        whisperBroken = true;
        warmPromise = null;
      });
  }
  return warmPromise;
}

/** True when SOME engine can listen here (whisper-capable or legacy SR). */
export function isSttSupported(): boolean {
  return wantWhisper() || isVoiceTalkSupported();
}

export type SttVoiceTalkOpts = {
  onState: (s: VoiceTalkState) => void;
  onFinal: (transcript: string) => void;
  onInterim?: (transcript: string) => void;
  /** Live input level 0..1 (whisper engine only; SR has no level data). */
  onLevel?: (level: number) => void;
  /** Captured audio is mid-transcription (whisper only) — the caller's listen
   *  window must not abort while this is true, or already-spoken words die. */
  onTranscribing?: (active: boolean) => void;
  lang?: string;
  silenceMs?: number;
};

/**
 * Engine-selecting factory honoring the legacy contract. Whisper path: mic is
 * acquired on start() and released on stop/abort/final — permission prompt
 * timing matches SpeechRecognition (at listen start, embedding-page origin).
 */
export function createSttVoiceTalk(opts: SttVoiceTalkOpts): VoiceTalkHandle {
  if (!wantWhisper()) {
    setHighPrecisionStt(false);
    return createVoiceTalk(opts);
  }
  setHighPrecisionStt(true);

  let session: SttSession | null = null;
  let mic: MediaStream | null = null;
  let active = false;
  let fallback: VoiceTalkHandle | null = null;

  const releaseAll = () => {
    session = null;
    if (mic) {
      releaseMic(mic);
      mic = null;
    }
  };

  const downgrade = (reason: string) => {
    try { console.warn("[BlueHat stt] whisper failed:", reason); } catch { /* ignore */ }
    releaseAll();
    if (isVoiceTalkSupported()) {
      // Hand THIS listen to browser SR so the visitor never faces a dead mic;
      // whisper retries next session (warmStt resets the flag).
      whisperBroken = true;
      setHighPrecisionStt(false);
      fallback = createVoiceTalk(opts);
      fallback.start();
      return;
    }
    // No SR here (e.g. Firefox / this bench browser): whisper IS the only
    // engine — do NOT mark it broken; surface error so the flow shows a hint
    // and the next listen retries fresh.
    opts.onState("error");
  };

  return {
    supported: true,
    start() {
      if (active) return;
      active = true;
      void (async () => {
        const got = await acquireMic();
        if (!active) {
          if (got.ok) releaseMic(got.stream);
          return;
        }
        if (!got.ok) {
          if (got.reason === "denied") {
            active = false;
            opts.onState("error");
          } else {
            active = false;
            downgrade("mic acquire failed");
          }
          return;
        }
        mic = got.stream;
        const engine = createWhisperEngine(getRuntime());
        session = engine({
          stream: mic,
          lang: opts.lang,
          silenceMs: opts.silenceMs,
          onState: (s: SttState) => {
            if (s === "idle") {
              active = false;
              releaseAll();
            }
            if (s === "error") {
              active = false;
              downgrade("engine session error");
              return;
            }
            opts.onState(s);
          },
          onInterim: opts.onInterim,
          onLevel: opts.onLevel,
          onTranscribing: opts.onTranscribing,
          onFinal: (t) => {
            active = false;
            releaseAll();
            opts.onFinal(t);
          },
        });
      })();
    },
    stop() {
      if (fallback) return fallback.stop();
      // Flush path: do NOT set active=false before session.stop() — whisper's
      // async transcribe used to see a dead session and drop the final (FM
      // "yes"/"both" never reached chat). session onFinal/idle releases mic.
      if (session) {
        session.stop();
        return;
      }
      // Still acquiring mic / model — cancel start so a late getUserMedia
      // does not open a zombie stream after the listen window closed.
      active = false;
      releaseAll();
    },
    abort() {
      if (fallback) return fallback.abort();
      active = false;
      session?.abort();
      releaseAll();
    },
  };
}
